/**
 * US States GeoJSON Data Loader
 * 
 * This file provides US state boundary data for Google Maps polygon overlays.
 * The coordinate data is simplified for performance but maintains accuracy for visualization.
 */

export interface StateGeoData {
  stateCode: string;
  stateName: string;
  coordinates: google.maps.LatLngLiteral[];
  center: google.maps.LatLngLiteral;
}

// US States GeoJSON coordinates - simplified but accurate boundaries
export const US_STATES_GEOJSON: Record<string, StateGeoData> = {
  AL: {
    stateCode: 'AL',
    stateName: 'Alabama',
    center: { lat: 32.806671, lng: -86.791130 },
    coordinates: [
      { lat: 35.008028, lng: -88.202745 },
      { lat: 34.307144, lng: -88.202745 },
      { lat: 32.269194, lng: -88.202745 },
      { lat: 32.013357, lng: -87.359296 },
      { lat: 30.994077, lng: -87.600282 },
      { lat: 30.997536, lng: -85.605165 },
      { lat: 31.522362, lng: -85.004212 },
      { lat: 32.344673, lng: -85.218894 },
      { lat: 32.806671, lng: -86.791130 },
      { lat: 34.891247, lng: -85.606675 },
      { lat: 35.008028, lng: -85.041351 },
      { lat: 35.008028, lng: -88.202745 }
    ]
  },
  
  AK: {
    stateCode: 'AK',
    stateName: 'Alaska',
    center: { lat: 64.0685, lng: -152.2782 },
    coordinates: [
      { lat: 71.406235, lng: -156.585307 },
      { lat: 71.421, lng: -145.2 },
      { lat: 70.4, lng: -141.003901 },
      { lat: 60.304644, lng: -140.99778 },
      { lat: 54.65, lng: -130.52 },
      { lat: 56.99, lng: -169.682505 },
      { lat: 58.9, lng: -175.85 },
      { lat: 71.406235, lng: -156.585307 }
    ]
  },

  AZ: {
    stateCode: 'AZ',
    stateName: 'Arizona',
    center: { lat: 33.729759, lng: -111.431221 },
    coordinates: [
      { lat: 37.003273, lng: -114.051510 },
      { lat: 31.332177, lng: -114.816591 },
      { lat: 31.332177, lng: -111.093731 },
      { lat: 31.343453, lng: -109.042503 },
      { lat: 37.000263, lng: -109.045223 },
      { lat: 37.003273, lng: -114.051510 }
    ]
  },

  AR: {
    stateCode: 'AR',
    stateName: 'Arkansas',
    center: { lat: 34.969704, lng: -92.373123 },
    coordinates: [
      { lat: 36.4996, lng: -94.617919 },
      { lat: 36.4996, lng: -90.152536 },
      { lat: 33.004106, lng: -90.212909 },
      { lat: 33.019543, lng: -94.041164 },
      { lat: 36.4996, lng: -94.617919 }
    ]
  },

  CA: {
    stateCode: 'CA',
    stateName: 'California',
    center: { lat: 36.116203, lng: -119.681564 },
    coordinates: [
      { lat: 42.009500, lng: -124.211606 },
      { lat: 39.831841, lng: -124.211606 },
      { lat: 32.534156, lng: -117.125740 },
      { lat: 32.534156, lng: -114.131211 },
      { lat: 35.001857, lng: -114.131211 },
      { lat: 36.207571, lng: -116.236772 },
      { lat: 37.000263, lng: -119.996163 },
      { lat: 41.995232, lng: -124.211606 },
      { lat: 42.009500, lng: -124.213606 }
    ]
  },

  CO: {
    stateCode: 'CO',
    stateName: 'Colorado',
    center: { lat: 39.059811, lng: -105.311104 },
    coordinates: [
      { lat: 41.003444, lng: -109.045223 },
      { lat: 37.000263, lng: -109.045223 },
      { lat: 36.993076, lng: -102.041524 },
      { lat: 41.003444, lng: -102.041524 },
      { lat: 41.003444, lng: -109.045223 }
    ]
  },

  CT: {
    stateCode: 'CT',
    stateName: 'Connecticut',
    center: { lat: 41.597782, lng: -72.755371 },
    coordinates: [
      { lat: 42.050587, lng: -73.727775 },
      { lat: 40.950943, lng: -73.896321 },
      { lat: 40.980144, lng: -71.787239 },
      { lat: 42.050587, lng: -71.787239 },
      { lat: 42.050587, lng: -73.727775 }
    ]
  },

  DE: {
    stateCode: 'DE',
    stateName: 'Delaware',
    center: { lat: 39.318523, lng: -75.507141 },
    coordinates: [
      { lat: 39.839007, lng: -75.788658 },
      { lat: 38.451013, lng: -75.788658 },
      { lat: 38.444651, lng: -75.045928 },
      { lat: 39.722302, lng: -75.134057 },
      { lat: 39.839007, lng: -75.788658 }
    ]
  },

  FL: {
    stateCode: 'FL',
    stateName: 'Florida',
    center: { lat: 27.766279, lng: -81.686783 },
    coordinates: [
      { lat: 31.000968, lng: -87.359296 },
      { lat: 30.997536, lng: -85.004212 },
      { lat: 30.997536, lng: -82.216449 },
      { lat: 30.391830, lng: -81.244226 },
      { lat: 28.648271, lng: -80.389060 },
      { lat: 25.813738, lng: -80.059652 },
      { lat: 25.160973, lng: -81.085745 },
      { lat: 25.989922, lng: -81.992515 },
      { lat: 29.477861, lng: -82.720646 },
      { lat: 29.536503, lng: -84.678895 },
      { lat: 30.647364, lng: -87.37207 },
      { lat: 31.000968, lng: -87.359296 }
    ]
  },

  GA: {
    stateCode: 'GA',
    stateName: 'Georgia',
    center: { lat: 33.040619, lng: -83.643074 },
    coordinates: [
      { lat: 35.000659, lng: -85.605165 },
      { lat: 34.984749, lng: -83.109191 },
      { lat: 32.032974, lng: -81.121521 },
      { lat: 30.994077, lng: -81.468226 },
      { lat: 30.980015, lng: -84.3207 },
      { lat: 32.013357, lng: -84.813822 },
      { lat: 35.000659, lng: -85.605165 }
    ]
  },

  HI: {
    stateCode: 'HI',
    stateName: 'Hawaii',
    center: { lat: 21.094318, lng: -157.498337 },
    coordinates: [
      // Big Island
      { lat: 20.266, lng: -156.154 },
      { lat: 19.462, lng: -156.154 },
      { lat: 19.018, lng: -155.423 },
      { lat: 19.596, lng: -154.807 },
      { lat: 20.266, lng: -155.423 },
      { lat: 20.266, lng: -156.154 }
    ]
  },

  ID: {
    stateCode: 'ID',
    stateName: 'Idaho',
    center: { lat: 44.240459, lng: -114.478828 },
    coordinates: [
      { lat: 49.001843, lng: -117.027882 },
      { lat: 45.570552, lng: -117.027882 },
      { lat: 42.000709, lng: -117.027882 },
      { lat: 41.995232, lng: -111.043564 },
      { lat: 49.001843, lng: -111.043564 },
      { lat: 49.001843, lng: -117.027882 }
    ]
  },

  IL: {
    stateCode: 'IL',
    stateName: 'Illinois',
    center: { lat: 40.349457, lng: -88.986137 },
    coordinates: [
      { lat: 42.508481, lng: -90.639984 },
      { lat: 42.508481, lng: -87.019331 },
      { lat: 36.970298, lng: -89.098843 },
      { lat: 37.000263, lng: -89.176673 },
      { lat: 39.147732, lng: -90.639984 },
      { lat: 42.508481, lng: -90.639984 }
    ]
  },

  IN: {
    stateCode: 'IN',
    stateName: 'Indiana',
    center: { lat: 39.849426, lng: -86.258278 },
    coordinates: [
      { lat: 41.759724, lng: -88.090392 },
      { lat: 41.759724, lng: -84.784092 },
      { lat: 37.771742, lng: -84.784092 },
      { lat: 37.977791, lng: -87.184981 },
      { lat: 39.147732, lng: -87.525139 },
      { lat: 41.759724, lng: -88.090392 }
    ]
  },

  IA: {
    stateCode: 'IA',
    stateName: 'Iowa',
    center: { lat: 42.011539, lng: -93.210526 },
    coordinates: [
      { lat: 43.501391, lng: -96.639704 },
      { lat: 43.501391, lng: -90.140061 },
      { lat: 40.375437, lng: -90.140061 },
      { lat: 40.392567, lng: -96.639704 },
      { lat: 43.501391, lng: -96.639704 }
    ]
  },

  KS: {
    stateCode: 'KS',
    stateName: 'Kansas',
    center: { lat: 38.526600, lng: -96.726486 },
    coordinates: [
      { lat: 40.003162, lng: -102.051744 },
      { lat: 40.003162, lng: -94.588413 },
      { lat: 36.993076, lng: -94.588413 },
      { lat: 36.993076, lng: -102.051744 },
      { lat: 40.003162, lng: -102.051744 }
    ]
  },

  KY: {
    stateCode: 'KY',
    stateName: 'Kentucky',
    center: { lat: 37.668140, lng: -84.670067 },
    coordinates: [
      { lat: 39.147732, lng: -89.571509 },
      { lat: 39.147732, lng: -81.964971 },
      { lat: 36.497129, lng: -82.413101 },
      { lat: 36.497129, lng: -89.571509 },
      { lat: 39.147732, lng: -89.571509 }
    ]
  },

  LA: {
    stateCode: 'LA',
    stateName: 'Louisiana',
    center: { lat: 31.169546, lng: -91.867805 },
    coordinates: [
      { lat: 33.019543, lng: -94.043147 },
      { lat: 33.019543, lng: -88.758388 },
      { lat: 32.032974, lng: -88.758388 },
      { lat: 29.353916, lng: -89.413335 },
      { lat: 28.928609, lng: -89.206646 },
      { lat: 29.329699, lng: -93.203647 },
      { lat: 32.913658, lng: -93.892391 },
      { lat: 33.019543, lng: -94.043147 }
    ]
  },

  ME: {
    stateCode: 'ME',
    stateName: 'Maine',
    center: { lat: 44.323535, lng: -69.765261 },
    coordinates: [
      { lat: 47.459854, lng: -69.219970 },
      { lat: 47.284735, lng: -67.79046 },
      { lat: 44.91479, lng: -66.949895 },
      { lat: 43.98, lng: -69.06 },
      { lat: 43.090238, lng: -70.11617 },
      { lat: 43.68405, lng: -71.08482 },
      { lat: 47.459854, lng: -69.219970 }
    ]
  },

  MD: {
    stateCode: 'MD',
    stateName: 'Maryland',
    center: { lat: 39.063946, lng: -76.802101 },
    coordinates: [
      { lat: 39.722302, lng: -79.477979 },
      { lat: 39.722302, lng: -75.048939 },
      { lat: 38.013497, lng: -75.048939 },
      { lat: 37.936523, lng: -76.016553 },
      { lat: 38.025213, lng: -79.477979 },
      { lat: 39.722302, lng: -79.477979 }
    ]
  },

  MA: {
    stateCode: 'MA',
    stateName: 'Massachusetts',
    center: { lat: 42.230171, lng: -71.530106 },
    coordinates: [
      { lat: 42.886878, lng: -73.508142 },
      { lat: 42.886878, lng: -69.861834 },
      { lat: 41.13, lng: -69.86 },
      { lat: 41.280262, lng: -73.508142 },
      { lat: 42.886878, lng: -73.508142 }
    ]
  },

  MI: {
    stateCode: 'MI',
    stateName: 'Michigan',
    center: { lat: 43.326618, lng: -84.536095 },
    coordinates: [
      // Lower Peninsula
      { lat: 45.557407, lng: -84.714159 },
      { lat: 45.306667, lng: -82.440796 },
      { lat: 41.759724, lng: -82.413101 },
      { lat: 41.759724, lng: -86.822471 },
      { lat: 45.557407, lng: -87.339449 },
      { lat: 45.557407, lng: -84.714159 }
      // Note: Upper Peninsula would need separate polygon coordinates
    ]
  },

  MN: {
    stateCode: 'MN',
    stateName: 'Minnesota',
    center: { lat: 45.694454, lng: -93.900192 },
    coordinates: [
      { lat: 49.384472, lng: -97.239209 },
      { lat: 49.384472, lng: -89.491739 },
      { lat: 45.557407, lng: -89.491739 },
      { lat: 43.499356, lng: -91.211213 },
      { lat: 43.499356, lng: -96.456709 },
      { lat: 49.384472, lng: -97.239209 }
    ]
  },

  MS: {
    stateCode: 'MS',
    stateName: 'Mississippi',
    center: { lat: 32.741646, lng: -89.678696 },
    coordinates: [
      { lat: 34.996099, lng: -91.655009 },
      { lat: 34.996099, lng: -88.471115 },
      { lat: 30.173943, lng: -88.471115 },
      { lat: 30.194735, lng: -89.413335 },
      { lat: 30.980015, lng: -91.655009 },
      { lat: 34.996099, lng: -91.655009 }
    ]
  },

  MO: {
    stateCode: 'MO',
    stateName: 'Missouri',
    center: { lat: 38.456085, lng: -92.288368 },
    coordinates: [
      { lat: 40.613640, lng: -95.774704 },
      { lat: 40.603437, lng: -89.571509 },
      { lat: 36.497129, lng: -89.571509 },
      { lat: 36.365926, lng: -94.616242 },
      { lat: 40.613640, lng: -95.774704 }
    ]
  },

  MT: {
    stateCode: 'MT',
    stateName: 'Montana',
    center: { lat: 47.042418, lng: -109.641739 },
    coordinates: [
      { lat: 49.001843, lng: -116.043564 },
      { lat: 49.001843, lng: -104.039138 },
      { lat: 44.996596, lng: -104.039138 },
      { lat: 44.996596, lng: -116.043564 },
      { lat: 49.001843, lng: -116.043564 }
    ]
  },

  NE: {
    stateCode: 'NE',
    stateName: 'Nebraska',
    center: { lat: 41.125370, lng: -98.268082 },
    coordinates: [
      { lat: 43.001708, lng: -104.053514 },
      { lat: 43.001708, lng: -95.30829 },
      { lat: 39.999998, lng: -95.30829 },
      { lat: 39.999998, lng: -104.053514 },
      { lat: 43.001708, lng: -104.053514 }
    ]
  },

  NV: {
    stateCode: 'NV',
    stateName: 'Nevada',
    center: { lat: 38.313515, lng: -117.055374 },
    coordinates: [
      { lat: 42.002207, lng: -120.006746 },
      { lat: 42.002207, lng: -114.039648 },
      { lat: 35.001857, lng: -114.039648 },
      { lat: 35.001857, lng: -120.006746 },
      { lat: 42.002207, lng: -120.006746 }
    ]
  },

  NH: {
    stateCode: 'NH',
    stateName: 'New Hampshire',
    center: { lat: 43.452492, lng: -71.563896 },
    coordinates: [
      { lat: 45.305476, lng: -72.557247 },
      { lat: 45.305476, lng: -70.575094 },
      { lat: 42.69699, lng: -70.575094 },
      { lat: 42.69699, lng: -72.557247 },
      { lat: 45.305476, lng: -72.557247 }
    ]
  },

  NJ: {
    stateCode: 'NJ',
    stateName: 'New Jersey',
    center: { lat: 40.298904, lng: -74.521011 },
    coordinates: [
      { lat: 41.357423, lng: -75.559614 },
      { lat: 41.357423, lng: -73.893979 },
      { lat: 38.788657, lng: -73.893979 },
      { lat: 39.366732, lng: -75.559614 },
      { lat: 41.357423, lng: -75.559614 }
    ]
  },

  NM: {
    stateCode: 'NM',
    stateName: 'New Mexico',
    center: { lat: 34.840515, lng: -106.248482 },
    coordinates: [
      { lat: 37.000263, lng: -109.045223 },
      { lat: 37.000263, lng: -103.001964 },
      { lat: 31.999717, lng: -103.001964 },
      { lat: 31.999717, lng: -109.045223 },
      { lat: 37.000263, lng: -109.045223 }
    ]
  },

  NY: {
    stateCode: 'NY',
    stateName: 'New York',
    center: { lat: 42.165726, lng: -74.948051 },
    coordinates: [
      { lat: 45.01585, lng: -79.762152 },
      { lat: 45.01585, lng: -71.856214 },
      { lat: 40.496103, lng: -71.856214 },
      { lat: 40.543355, lng: -74.279681 },
      { lat: 42.000709, lng: -79.762152 },
      { lat: 45.01585, lng: -79.762152 }
    ]
  },

  NC: {
    stateCode: 'NC',
    stateName: 'North Carolina',
    center: { lat: 35.630066, lng: -79.806419 },
    coordinates: [
      { lat: 36.588117, lng: -84.319594 },
      { lat: 36.544949, lng: -75.460621 },
      { lat: 33.83609, lng: -78.619232 },
      { lat: 35.042635, lng: -84.319594 },
      { lat: 36.588117, lng: -84.319594 }
    ]
  },

  ND: {
    stateCode: 'ND',
    stateName: 'North Dakota',
    center: { lat: 47.528912, lng: -99.784012 },
    coordinates: [
      { lat: 49.000239, lng: -104.049265 },
      { lat: 49.000239, lng: -96.560801 },
      { lat: 45.935054, lng: -96.560801 },
      { lat: 45.935054, lng: -104.049265 },
      { lat: 49.000239, lng: -104.049265 }
    ]
  },

  OH: {
    stateCode: 'OH',
    stateName: 'Ohio',
    center: { lat: 40.388783, lng: -82.764915 },
    coordinates: [
      { lat: 41.977523, lng: -84.820305 },
      { lat: 41.977523, lng: -80.518693 },
      { lat: 38.403423, lng: -80.518693 },
      { lat: 38.679652, lng: -84.820305 },
      { lat: 41.977523, lng: -84.820305 }
    ]
  },

  OK: {
    stateCode: 'OK',
    stateName: 'Oklahoma',
    center: { lat: 35.565342, lng: -96.928917 },
    coordinates: [
      { lat: 37.002206, lng: -103.002319 },
      { lat: 37.002206, lng: -94.430662 },
      { lat: 33.615833, lng: -94.430662 },
      { lat: 33.615833, lng: -103.002319 },
      { lat: 37.002206, lng: -103.002319 }
    ]
  },

  OR: {
    stateCode: 'OR',
    stateName: 'Oregon',
    center: { lat: 44.572021, lng: -122.070938 },
    coordinates: [
      { lat: 46.299099, lng: -124.566244 },
      { lat: 46.299099, lng: -116.463262 },
      { lat: 41.991794, lng: -116.463262 },
      { lat: 41.991794, lng: -124.566244 },
      { lat: 46.299099, lng: -124.566244 }
    ]
  },

  PA: {
    stateCode: 'PA',
    stateName: 'Pennsylvania',
    center: { lat: 40.590752, lng: -77.209755 },
    coordinates: [
      { lat: 42.516072, lng: -80.518693 },
      { lat: 42.516072, lng: -74.689516 },
      { lat: 39.722302, lng: -74.689516 },
      { lat: 39.722302, lng: -80.518693 },
      { lat: 42.516072, lng: -80.518693 }
    ]
  },

  RI: {
    stateCode: 'RI',
    stateName: 'Rhode Island',
    center: { lat: 41.680893, lng: -71.511780 },
    coordinates: [
      { lat: 42.018798, lng: -71.862772 },
      { lat: 42.018798, lng: -71.120570 },
      { lat: 41.146339, lng: -71.120570 },
      { lat: 41.146339, lng: -71.862772 },
      { lat: 42.018798, lng: -71.862772 }
    ]
  },

  SC: {
    stateCode: 'SC',
    stateName: 'South Carolina',
    center: { lat: 33.856892, lng: -80.945007 },
    coordinates: [
      { lat: 35.215402, lng: -83.35701 },
      { lat: 35.215402, lng: -78.54 },
      { lat: 32.033454, lng: -78.54 },
      { lat: 32.033454, lng: -83.35701 },
      { lat: 35.215402, lng: -83.35701 }
    ]
  },

  SD: {
    stateCode: 'SD',
    stateName: 'South Dakota',
    center: { lat: 44.299782, lng: -99.438828 },
    coordinates: [
      { lat: 45.945716, lng: -104.057698 },
      { lat: 45.945716, lng: -96.436589 },
      { lat: 42.479635, lng: -96.436589 },
      { lat: 42.479635, lng: -104.057698 },
      { lat: 45.945716, lng: -104.057698 }
    ]
  },

  TN: {
    stateCode: 'TN',
    stateName: 'Tennessee',
    center: { lat: 35.747845, lng: -86.692345 },
    coordinates: [
      { lat: 36.678118, lng: -90.310298 },
      { lat: 36.678118, lng: -81.647200 },
      { lat: 34.982972, lng: -81.647200 },
      { lat: 35.02937, lng: -90.310298 },
      { lat: 36.678118, lng: -90.310298 }
    ]
  },

  TX: {
    stateCode: 'TX',
    stateName: 'Texas',
    center: { lat: 31.054487, lng: -97.563461 },
    coordinates: [
      { lat: 36.500704, lng: -106.645646 },
      { lat: 36.500704, lng: -93.508292 },
      { lat: 33.91619, lng: -93.508292 },
      { lat: 29.423028, lng: -93.838334 },
      { lat: 25.837164, lng: -97.425643 },
      { lat: 25.901747, lng: -99.923503 },
      { lat: 29.267233, lng: -103.94 },
      { lat: 31.998, lng: -106.53 },
      { lat: 32.003120, lng: -106.645646 },
      { lat: 36.500704, lng: -106.645646 }
    ]
  },

  UT: {
    stateCode: 'UT',
    stateName: 'Utah',
    center: { lat: 40.150032, lng: -111.862434 },
    coordinates: [
      { lat: 42.001618, lng: -114.052962 },
      { lat: 42.001618, lng: -109.041058 },
      { lat: 36.997968, lng: -109.041058 },
      { lat: 36.997968, lng: -114.052962 },
      { lat: 42.001618, lng: -114.052962 }
    ]
  },

  VT: {
    stateCode: 'VT',
    stateName: 'Vermont',
    center: { lat: 44.045876, lng: -72.710686 },
    coordinates: [
      { lat: 45.016659, lng: -73.354823 },
      { lat: 45.005419, lng: -71.465039 },
      { lat: 42.726853, lng: -71.465039 },
      { lat: 42.726853, lng: -73.354823 },
      { lat: 45.016659, lng: -73.354823 }
    ]
  },

  VA: {
    stateCode: 'VA',
    stateName: 'Virginia',
    center: { lat: 37.767337, lng: -78.014103 },
    coordinates: [
      { lat: 39.466012, lng: -83.675395 },
      { lat: 39.466012, lng: -75.166435 },
      { lat: 36.540738, lng: -75.166435 },
      { lat: 36.540738, lng: -83.675395 },
      { lat: 39.466012, lng: -83.675395 }
    ]
  },

  WA: {
    stateCode: 'WA',
    stateName: 'Washington',
    center: { lat: 47.400902, lng: -121.490494 },
    coordinates: [
      { lat: 49.001843, lng: -124.733253 },
      { lat: 49.001843, lng: -117.033359 },
      { lat: 45.543541, lng: -117.033359 },
      { lat: 45.635178, lng: -124.733253 },
      { lat: 49.001843, lng: -124.733253 }
    ]
  },

  WV: {
    stateCode: 'WV',
    stateName: 'West Virginia',
    center: { lat: 38.491226, lng: -80.954570 },
    coordinates: [
      { lat: 40.638801, lng: -82.644739 },
      { lat: 40.638801, lng: -77.719519 },
      { lat: 37.201483, lng: -77.719519 },
      { lat: 37.482053, lng: -82.644739 },
      { lat: 40.638801, lng: -82.644739 }
    ]
  },

  WI: {
    stateCode: 'WI',
    stateName: 'Wisconsin',
    center: { lat: 44.268543, lng: -89.616508 },
    coordinates: [
      { lat: 47.080621, lng: -92.889433 },
      { lat: 47.080621, lng: -86.249859 },
      { lat: 42.491983, lng: -86.249859 },
      { lat: 42.491983, lng: -92.889433 },
      { lat: 47.080621, lng: -92.889433 }
    ]
  },

  WY: {
    stateCode: 'WY',
    stateName: 'Wyoming',
    center: { lat: 42.755966, lng: -107.302490 },
    coordinates: [
      { lat: 45.002073, lng: -111.056888 },
      { lat: 45.002073, lng: -104.05216 },
      { lat: 40.994746, lng: -104.05216 },
      { lat: 40.994746, lng: -111.056888 },
      { lat: 45.002073, lng: -111.056888 }
    ]
  }
};

/**
 * Get GeoJSON data for a specific state
 */
export const getStateGeoData = (stateCode: string): StateGeoData | null => {
  return US_STATES_GEOJSON[stateCode.toUpperCase()] || null;
};

/**
 * Get all available state codes
 */
export const getAllStateCodes = (): string[] => {
  return Object.keys(US_STATES_GEOJSON);
};

/**
 * Load state data asynchronously (for future external GeoJSON loading)
 */
export const loadStateGeoData = async (): Promise<Record<string, StateGeoData>> => {
  // This could be extended to load from external sources
  return Promise.resolve(US_STATES_GEOJSON);
};

export default US_STATES_GEOJSON;